/** @format */

import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Injectable, PLATFORM_ID, REQUEST_CONTEXT, computed, inject } from '@angular/core';
import { HttpService, KillswitchService } from '@shared/data-access/services';
import {
  ProductListApiService,
  ProductListCompareService,
  ProductListMlService,
  ProductListUIService,
} from '@plp/data-access/services';
import { catchError, map, take } from 'rxjs/operators';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  selectFeatureFlags,
  srlpResolverAPIDataInitialized,
  srlpResolverOnLoadDataInitialized,
  srlpResolverProductListFetchErrored,
  srlpResolverProductListFetchNoData,
} from '@plp/data-access/state';

import { ProductList } from '@plp/data-access/interfaces';
import { SeoService } from '@seo/data-access';
import { Store } from '@ngrx/store';
import { Title } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { searchDropdownValues } from '@plp/data-access/constants';
import { setCommonPageType } from '@state/data-access';

// eslint-disable-next-line @angular-eslint/use-injectable-provided-in
@Injectable()
export class SearchResultsListPageResolveGuard {
  private readonly productListApiService = inject(ProductListApiService);

  private readonly productListUIService = inject(ProductListUIService);

  private readonly killSwitchService = inject(KillswitchService);

  private readonly router = inject(Router);

  private readonly seoService = inject(SeoService);

  private readonly store = inject(Store);

  private readonly title = inject(Title);

  private readonly plpCompareService = inject(ProductListCompareService);

  private readonly plpMlService = inject(ProductListMlService);

  private readonly platformId = inject(PLATFORM_ID);

  private readonly httpService = inject(HttpService);

  private readonly propagateToServer = inject<{
    PROPAGATE_ERROR_TO_SERVER: (propagatedError: { errorType: string; redirectUrl?: string }) => {
      errorType: string;
      redirectUrl?: string;
    };
  }>(REQUEST_CONTEXT, { optional: true })?.PROPAGATE_ERROR_TO_SERVER;

  private readonly featureFlags = this.store.selectSignal(selectFeatureFlags);

  private readonly compareCheckboxEnabled = computed(
    () => this.featureFlags().compareCheckboxEnabled,
  );

  fulfillmentFacetsEnabled = false;

  constructor() {
    this.store.dispatch(setCommonPageType({ pageType: 'srlp' }));

    const featureFlags = this.killSwitchService.killSwitchesForCurrentChain();
    this.fulfillmentFacetsEnabled = featureFlags.fulfillmentFacetsEnabled;
  }

  resolve(route: ActivatedRouteSnapshot): Observable<ProductList | null> {
    this.title.setTitle('Search Results');

    const invalidQueryParams = this.productListApiService.searchRequestParamValidator(
      route.queryParams,
    );

    const selectedSortNumber: number = Number.parseInt(route.queryParams.selectedSort, 10);

    if (invalidQueryParams) {
      this.router.navigateByUrl('/404', { skipLocationChange: false });
      return of(null);
    } else {
      const selectedSort: { label: string; value: number } = route.queryParams?.selectedSort
        ? {
            label: searchDropdownValues[selectedSortNumber].label,
            value: selectedSortNumber,
          }
        : { label: searchDropdownValues[0].label, value: 0 };

      const pageNumber = this.productListUIService.getPageNumber(route.queryParams?.pageNumber);
      const pageSize = this.productListUIService.getPageSize(route.queryParams?.pageSize);
      const selectedFilters = this.productListApiService.formatFiltersFromURL(
        route.queryParams.filterFacets,
      );

      this.store.dispatch(
        srlpResolverOnLoadDataInitialized({
          data: { pageNumber, pageSize, selectedFilters, selectedSort },
        }),
      );

      this.seoService.createRobotsTag({ value: 'Y' }, { value: 'N' }, { value: 'Y' });

      const queryParams = this.productListApiService.setProductListParamDataForApi({
        selectedCategoryId: null,
        searchTerm: route.queryParams.searchTerm,
        pageNumber,
        selectedFilters,
        pageSize,
        includeFulfillmentFacets: this.fulfillmentFacetsEnabled,
        selectedStoreNumber: route.queryParams.selectedStore,
        zipcode: route.queryParams.zipCode,
      });

      return this.productListApiService.getProductListDataFromApi$(queryParams, 1).pipe(
        take(1),
        map((productList: ProductList | null) => {
          this.handlePageRedirect(productList);

          if (productList?.totalCount === undefined) {
            this.store.dispatch(srlpResolverProductListFetchNoData());
            return null;
          }

          this.plpMlService.generateNewMlSearchEventParentUUID();

          const compareBarProducts = this.compareCheckboxEnabled()
            ? this.plpCompareService.getCompareListFromSessionStorage()
            : [];

          this.store.dispatch(
            srlpResolverAPIDataInitialized({
              data: {
                productList,
                compareBarProducts,
              },
            }),
          );

          return productList;
        }),
        catchError((error) => {
          if (isPlatformServer(this.platformId) && error?.errorType === 'redirect') {
            throw error;
          }

          this.productListApiService.logProductListApiError(error, 'srlp-guard', {
            searchTerm: route.queryParams.searchTerm,
          });
          this.store.dispatch(srlpResolverProductListFetchErrored());
          return of(null);
        }),
      );
    }
  }

  // Future resiliency if we move Search to SSR, we want to be able to abort render early for a redirect
  handlePageRedirect(productListData: ProductList | null): void {
    if (!productListData?.searchVO?.landingPageURL) {
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      // If the landingPageURL is present, redirect to the PLP, /search/ -> /f/
      this.productListUIService.redirectToPLPOrPDP(productListData.searchVO.landingPageURL);
      return;
    }

    if (this.propagateToServer) {
      this.propagateToServer({
        errorType: 'redirect',
        redirectUrl: productListData.searchVO.landingPageURL,
      });
    } else {
      this.httpService.redirect(`${productListData.searchVO.landingPageURL}`);
    }

    const redirectError = new Error('SRLP Redirect error') as Error & {
      errorType: string;
      redirectUrl: string;
    };

    redirectError.errorType = 'redirect';
    redirectError.redirectUrl = productListData.searchVO.landingPageURL;

    throw redirectError;
  }
}
